import { useState, useEffect } from 'react';

export interface GuestFormData {
  l_name: string;
  f_name: string;
  p_name: string;
  company: string;
  role: string;
  country: string;
  email: string;
}

interface AddGuestModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    l_name: string;
    f_name: string;
    p_name: string;
    company: string;
    role: string;
    country: string;
    email: string;
  }) => void;
  saving?: boolean;
  mode?: 'add' | 'edit';
  initialData?: GuestFormData | null;
}

export function AddGuestModal({ open, onClose, onSave, saving, mode = 'add', initialData }: AddGuestModalProps) {
  const isEdit = mode === 'edit';
  const [lName, setLName] = useState('');
  const [fName, setFName] = useState('');
  const [pName, setPName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setLName(initialData.l_name);
        setFName(initialData.f_name);
        setPName(initialData.p_name);
        setCompany(initialData.company);
        setRole(initialData.role);
        setCountry(initialData.country);
        setEmail(initialData.email);
      } else {
        setLName('');
        setFName('');
        setPName('');
        setCompany('');
        setRole('');
        setCountry('');
        setEmail('');
      }
    }
  }, [open, isEdit, initialData]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!lName.trim() || !fName.trim()) return;
    onSave({
      l_name: lName.trim(),
      f_name: fName.trim(),
      p_name: pName.trim(),
      company: company.trim(),
      role: role.trim(),
      country: country.trim(),
      email: email.trim(),
    });
  };

  const resetForm = () => {
    setLName('');
    setFName('');
    setPName('');
    setCompany('');
    setRole('');
    setCountry('');
    setEmail('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const inputStyle = 'w-full rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text placeholder:text-ct-text-muted/50 focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-full max-w-lg rounded-xl bg-ct-surface border border-ct-border shadow-ct-md p-6">
        <h3 className="mb-4 text-lg font-bold text-ct-text">{isEdit ? 'Редактирование участника' : 'Добавление участника'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Фамилия *</label>
              <input
                type="text"
                value={lName}
                onChange={(e) => setLName(e.target.value)}
                required
                className={inputStyle}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Имя *</label>
              <input
                type="text"
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                required
                className={inputStyle}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-ct-text">Отчество</label>
            <input
              type="text"
              value={pName}
              onChange={(e) => setPName(e.target.value)}
              className={inputStyle}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Компания</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Должность</label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={inputStyle}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Страна</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-ct-text">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputStyle}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted transition-all duration-200 hover:bg-indigo-50 hover:text-ct-primary active:scale-[0.98]"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving || !lName.trim() || !fName.trim()}
              className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-5 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
